"use client"

import { useEffect, type RefObject } from "react"

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export const useFocusTrap = (ref: RefObject<HTMLElement | null>, active: boolean) => {
  useEffect(() => {
    const node = ref.current
    if (!active || !node) return
    const restoreTo = document.activeElement as HTMLElement | null
    const focusable = () => Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((element) => element.getClientRects().length > 0)

    if (!node.contains(document.activeElement)) focusable().at(0)?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return
      const items = focusable()
      const first = items.at(0)
      const last = items.at(-1)
      if (!first || !last) {
        event.preventDefault()
        return
      }
      if (!node.contains(document.activeElement)) {
        event.preventDefault()
        ;(event.shiftKey ? last : first).focus()
        return
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
        return
      }
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener("keydown", onKeyDown, true)
    return () => {
      document.removeEventListener("keydown", onKeyDown, true)
      if (restoreTo?.isConnected) restoreTo.focus()
    }
  }, [ref, active])
}
